import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetSpecialOfferQuery,
  useUpdateSpecialOfferMutation,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import ImageUpload from "../components/ImageUpload";
import Icon from "../components/Icon";
import RichTextEditor from "../components/RichTextEditor";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const EditSpecialOffer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();

  // RTK Query hooks
  const { data: offerData, isLoading: loading } = useGetSpecialOfferQuery(id);
  const [updateOffer, { isLoading: saving }] = useUpdateSpecialOfferMutation();

  const [saveStatus, setSaveStatus] = useState("");
  const messageType = useMessageType(saveStatus);

  const [formData, setFormData] = useState({
    title: "",
    titleAr: "",
    content: "",
    contentAr: "",
    image: null,
    is_active: true,
    keywords: "",
  });

  const [originalOffer, setOriginalOffer] = useState(null);

  // Load offer data when fetched
  useEffect(() => {
    if (offerData && offerData.offer) {
      const offer = offerData.offer;
      setOriginalOffer(offer);
      setFormData({
        title: offer.title || "",
        titleAr: offer.title_ar || "",
        content: offer.content || "",
        contentAr: offer.content_ar || "",
        image: offer.image_url || null,
        is_active: offer.is_active !== false,
        keywords: offer.keywords || "",
      });
    }
  }, [offerData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      setSaveStatus(t("addEditOffer.pleaseLoginToSave"));
      return;
    }

    // Validate form
    if (!formData.title.trim()) {
      setSaveStatus(t("addEditOffer.pleaseEnterTitle"));
      return;
    }

    if (!formData.content.trim()) {
      setSaveStatus(t("addEditOffer.pleaseEnterContent"));
      return;
    }

    setSaveStatus("");

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("titleAr", formData.titleAr);
      submitData.append("content", formData.content);
      submitData.append("contentAr", formData.contentAr);
      submitData.append("is_active", formData.is_active ? "1" : "0");
      submitData.append("keywords", formData.keywords || "");

      // Only append image if it's a new file (not existing URL)
      if (formData.image && typeof formData.image === "object") {
        submitData.append("image", formData.image);
      }

      const result = await updateOffer({ id, body: submitData }).unwrap();

      if (result.success) {
        setSaveStatus(t("addEditOffer.offerUpdatedSuccessfully"));
        setTimeout(() => {
          navigate("/dashboard/special-offers");
        }, 1500);
      } else {
        setSaveStatus(t("addEditOffer.errorUpdatingOffer"));
      }
    } catch (error) {
      console.error("Error updating offer:", error);
      setSaveStatus(
        error.data?.message || t("addEditOffer.errorUpdatingOffer")
      );
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/special-offers");
  };

  if (loading) {
    return (
      <div className="add-special-offer">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("addEditOffer.loadingOffer")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="add-special-offer">
        <div className="auth-required">
          <h2>{t("addEditOffer.authenticationRequired")}</h2>
          <p>{t("addEditOffer.pleaseLoginToEdit")}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            {t("addEditOffer.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  if (!originalOffer) {
    return (
      <div className="add-special-offer">
        <div className="auth-required">
          <h2>{t("addEditOffer.offerNotFound")}</h2>
          <p>{t("addEditOffer.offerNotFoundMessage")}</p>
          <button
            className="login-btn"
            onClick={() => navigate("/dashboard/special-offers")}
          >
            {t("addEditOffer.backToOffers")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-special-offer">
      <div className="page-header">
        <div className="header-content">
          <button type="button" className="back-btn" onClick={handleCancel}>
            <Icon name="arrow-left" size="16px" />
            {t("addEditOffer.backToOffers")}
          </button>
          <h1>{t("addEditOffer.editSpecialOffer")}</h1>
          <p>{t("addEditOffer.updateOfferDetails")}</p>
        </div>
      </div>

      <div className="form-container">
        <div className="form-card">
          <div className="form-content">
            <div className="form-group">
              <label>{t("addEditOffer.offerTitleEnglish")} *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder={t("addEditOffer.enterOfferTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.offerTitleArabic")}</label>
              <input
                type="text"
                value={formData.titleAr}
                onChange={(e) => handleInputChange("titleAr", e.target.value)}
                placeholder={t("addEditOffer.enterOfferTitleArabic")}
                className="form-input"
                dir="rtl"
              />
            </div>

            <div className="form-group">
              <ImageUpload
                label={t("addEditOffer.offerImage")}
                value={formData.image}
                onChange={(file) => handleInputChange("image", file)}
                className="image-upload-modern"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.offerContentEnglish")} *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => handleInputChange("content", content)}
                placeholder={t("addEditOffer.enterOfferDetails")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.offerContentArabic")}</label>
              <RichTextEditor
                value={formData.contentAr}
                onChange={(content) => handleInputChange("contentAr", content)}
                placeholder={t("addEditOffer.enterOfferDetailsArabic")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.keywords")}</label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => handleInputChange("keywords", e.target.value)}
                placeholder={t("addEditOffer.enterKeywords")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditOffer.keywordsHint")}
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                />
                <span>{t("addEditOffer.activeOffer")}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <StatusMessage
          message={saveStatus}
          type={messageType}
          show={!!saveStatus}
          autoClose={messageType === "success"}
          autoCloseDelay={3000}
          onClose={() => setSaveStatus("")}
        />
        <div className="save-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            {t("addEditOffer.cancel")}
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner small"></div>
                {t("addEditOffer.updating")}
              </>
            ) : (
              <>
                <Icon name="save" size="16px" />
                {t("addEditOffer.updateOffer")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSpecialOffer;
